import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { ArrowLeft, ArrowRight, Save, Clock, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import AssessmentTokenUI from './AssessmentTokenUI';
import PairwiseAssessment from './PairwiseAssessment';
import AssessmentMethodSelector from './AssessmentMethodSelector';
import EmployeeAspirationalQuestionnaire from './EmployeeAspirationalQuestionnaire';
import logger from '../utils/logger';

const AssessmentFlow = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, metaCapitals, t, changeLanguage } = useLanguage();
  
  // State
  const [assessment, setAssessment] = useState(null);
  const [currentSection, setCurrentSection] = useState('personal_data');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Token distribution state
  const [ofertas, setOfertas] = useState({
    capital_fisico: 0,
    capital_humano: 0,
    capital_manufacturado: 0,
    capital_financiero: 0,
    capital_natural: 0,
    capital_cultural: 0,
    capital_social: 0,
    capital_conocimiento: 0,
    capital_psicologico: 0,
    capital_espiritual: 0
  });
  
  const [requerimientos, setRequerimientos] = useState({
    capital_fisico: 0,
    capital_humano: 0,
    capital_manufacturado: 0,
    capital_financiero: 0,
    capital_natural: 0,
    capital_cultural: 0,
    capital_social: 0,
    capital_conocimiento: 0,
    capital_psicologico: 0,
    capital_espiritual: 0
  });

  // Assessment method state
  const [assessmentMethod, setAssessmentMethod] = useState('tokens'); // 'tokens' or 'pairwise'
  const [methodSelected, setMethodSelected] = useState(false);

  // Assessment sections
  const sections = [
    'personal_data',
    'introduction',
    'method_selection', // New section for method selection
    'offers',
    'needs',
    'aspirational', // MEVIA v5.0 - Employee aspirational questionnaire
    'validation',
    'results'
  ];
  
  // State for aspirational questionnaire completion
  const [aspirationalCompleted, setAspirationalCompleted] = useState(false);

  // Load assessment data
  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  // Auto-save every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (assessment && (currentSection === 'offers' || currentSection === 'needs')) {
        autoSave();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [assessment, currentSection, ofertas, requerimientos]);

  // Load previous progress
  useEffect(() => {
    if (assessment) {
      loadProgress();
    }
  }, [assessment]);

  const loadAssessment = async () => {
    try {
      const response = await axios.get(`/assessments/${assessmentId}`);
      if (response.data.success) {
        const assessmentData = response.data.data;
        
        
        setAssessment(assessmentData);
        setProgress(assessmentData.progreso_porcentaje);
        
        if (assessmentData.seccion_actual) {
          setCurrentSection(assessmentData.seccion_actual);
        }
        
        // IMPORTANT: Load ofertas and requerimientos into state
        // Handle null values by using default structure
        if (assessmentData.ofertas && assessmentData.ofertas !== null) {
          setOfertas(assessmentData.ofertas);
        } else {
        }
        
        if (assessmentData.requerimientos && assessmentData.requerimientos !== null) {
          setRequerimientos(assessmentData.requerimientos);
        } else {
        }
        
        // IMPORTANT: Load evaluation_method if it was previously selected
        if (assessmentData.evaluation_method) {
          setAssessmentMethod(assessmentData.evaluation_method);
          // If method was already selected (not default 'tokens' with no progress), mark as selected
          if (assessmentData.evaluation_method === 'pairwise' || assessmentData.progreso_porcentaje > 0) {
            setMethodSelected(true);
          }
        }
        
      } else {
        toast.error(t('error_loading_assessment', 'Error al cargar evaluación'));
        navigate('/dashboard');
      }
    } catch (error) {
      logger.error('Error loading assessment:', error);
      toast.error(t('error_loading_assessment', 'Error al cargar evaluación'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      // Load data directly from the assessment endpoint, not autosave
      const response = await axios.get(`/assessments/${assessmentId}`);
      if (response.data.success && response.data.data) {
        const assessmentData = response.data.data;
        
        
        // Load ofertas data if exists
        if (assessmentData.ofertas) {
          setOfertas(assessmentData.ofertas);
        }
        
        // Load requerimientos data if exists  
        if (assessmentData.requerimientos) {
          setRequerimientos(assessmentData.requerimientos);
        }
        
        if (assessmentData.seccion_actual) {
          setCurrentSection(assessmentData.seccion_actual);
        }
        
        // Show toast that progress was restored
        if (assessmentData.ofertas || assessmentData.requerimientos) {
          toast.success(
            t('progress_restored', 'Progreso anterior restaurado')
          );
        }
      }
    } catch (error) {
    }
  };

  const autoSave = useCallback(async () => {
    if (!assessment) return;
    
    try {
      setSaving(true);
      await axios.post(`/assessments/${assessmentId}/autosave`, {
        assessment_id: assessmentId,
        data_json: {
          ofertas,
          requerimientos,
          current_section: currentSection
        },
        seccion: currentSection
      });
    } catch (error) {
      logger.error('Auto-save error:', error);
    } finally {
      setSaving(false);
    }
  }, [assessmentId, ofertas, requerimientos, currentSection, assessment]);

  const updateProgress = async (newSection, progressPercentage) => {
    try {
      const updateData = {
        seccion_actual: newSection,
        progreso_porcentaje: progressPercentage
      };

      // Include token data if we're in the relevant sections
      if (newSection === 'offers' || currentSection === 'offers') {
        updateData.ofertas = ofertas;
      }
      if (newSection === 'needs' || currentSection === 'needs') {
        updateData.requerimientos = requerimientos;
      }

      await axios.put(`/assessments/${assessmentId}`, updateData);
      setProgress(progressPercentage);
    } catch (error) {
      logger.error('Error updating progress:', error);
    }
  };

  const calculateTotalTokens = (distribution) => {
    return Object.values(distribution).reduce((sum, value) => sum + value, 0);
  };

  const handleTokenChange = (capital, value, isOferta = true) => {
    const setter = isOferta ? setOfertas : setRequerimientos;
    const current = isOferta ? ofertas : requerimientos;
    
    // Ensure value doesn't exceed limits (now 0-100 range)
    const newValue = Math.max(0, Math.min(value, 100));
    
    setter({
      ...current,
      [capital]: newValue
    });
  };

  const resetTokens = (isOferta = true) => {
    const setter = isOferta ? setOfertas : setRequerimientos;
    const resetState = {
      capital_fisico: 0,
      capital_humano: 0,
      capital_manufacturado: 0,
      capital_financiero: 0,
      capital_natural: 0,
      capital_cultural: 0,
      capital_social: 0,
      capital_conocimiento: 0,
      capital_psicologico: 0,
      capital_espiritual: 0
    };
    setter(resetState);
    toast.success(t('tokens_reset', 'Tokens reiniciados'));
  };

  const distributeEvenly = (isOferta = true) => {
    const setter = isOferta ? setOfertas : setRequerimientos;
    const evenDistribution = {
      capital_fisico: 10,
      capital_humano: 10,
      capital_manufacturado: 10,
      capital_financiero: 10,
      capital_natural: 10,
      capital_cultural: 10,
      capital_social: 10,
      capital_conocimiento: 10,
      capital_psicologico: 10,
      capital_espiritual: 10
    };
    setter(evenDistribution);
    toast.success(t('tokens_distributed_evenly', 'Tokens distribuidos equitativamente'));
  };

  const adjustToTotal = (isOferta = true) => {
    const current = isOferta ? ofertas : requerimientos;
    const setter = isOferta ? setOfertas : setRequerimientos;
    const totalTokens = calculateTotalTokens(current);
    
    if (totalTokens === 100) return;
    
    // Find non-zero values to adjust
    const nonZeroEntries = Object.entries(current).filter(([_, value]) => value > 0);
    
    if (nonZeroEntries.length === 0) {
      distributeEvenly(isOferta);
      return;
    }
    
    const difference = 100 - totalTokens;
    const adjustmentPerEntry = Math.floor(difference / nonZeroEntries.length);
    const remainder = difference % nonZeroEntries.length;
    
    const adjusted = { ...current };
    
    nonZeroEntries.forEach(([capital, value], index) => {
      const adjustment = adjustmentPerEntry + (index < remainder ? Math.sign(difference) : 0);
      const newValue = Math.max(1, Math.min(50, value + adjustment));
      adjusted[capital] = newValue;
    });
    
    setter(adjusted);
    toast.success(t('tokens_adjusted', 'Tokens ajustados al total'));
  };

  const nextSection = async () => {
    const currentIndex = sections.indexOf(currentSection);
    if (currentIndex < sections.length - 1) {
      const newSection = sections[currentIndex + 1];
      setCurrentSection(newSection);
      const newProgress = ((currentIndex + 2) / sections.length) * 100;
      
      // If moving from method_selection, save the selected method
      if (currentSection === 'method_selection') {
        await saveEvaluationMethod();
      }
      
      // If reaching results section, mark as completed
      if (newSection === 'results') {
        completeAssessment(newProgress);
      } else {
        updateProgress(newSection, newProgress);
      }
    }
  };
  
  // Save the selected evaluation method to the backend
  const saveEvaluationMethod = async () => {
    try {
      await axios.put(`/assessments/${assessmentId}`, {
        evaluation_method: assessmentMethod
      });
    } catch (error) {
      logger.error('Error saving evaluation method:', error);
    }
  };

  const completeAssessment = async (finalProgress = 100) => {
    try {
      // Validate token distribution before completing
      const totalOfertas = calculateTotalTokens(ofertas);
      const totalRequerimientos = calculateTotalTokens(requerimientos);
      
      if (totalOfertas !== 100) {
        toast.error(
          t('messages.offers_token_error', `Error: Los tokens de ofertas suman ${totalOfertas}, deben ser exactamente 100`)
        );
        return;
      }
      
      if (totalRequerimientos !== 100) {
        toast.error(
          t('messages.requirements_token_error', `Error: Los tokens de requerimientos suman ${totalRequerimientos}, deben ser exactamente 100`)
        );
        return;
      }

      // Validate minimum 1 token per capital
      const allCapitalKeys = [
        'capital_fisico', 'capital_humano', 'capital_manufacturado',
        'capital_financiero', 'capital_natural', 'capital_cultural',
        'capital_social', 'capital_conocimiento', 'capital_psicologico',
        'capital_espiritual'
      ];
      
      for (const key of allCapitalKeys) {
        if (ofertas[key] < 1) {
          toast.error(t('assessment_ui.min_tokens_warning', 'Cada capital debe tener al menos 1 token en Ofertas'));
          return;
        }
        if (requerimientos[key] < 1) {
          toast.error(t('assessment_ui.min_tokens_warning', 'Cada capital debe tener al menos 1 token en Requerimientos'));
          return;
        }
      }

      const updateData = {
        seccion_actual: 'results',
        progreso_porcentaje: finalProgress,
        ofertas: ofertas,
        requerimientos: requerimientos
      };

      // Debug logging

      // Call backend to mark as completed
      await axios.post(`/assessments/${assessmentId}/complete`, updateData);
      setProgress(finalProgress);
      
      toast.success(
        language === 'es' 
          ? '¡Evaluación completada exitosamente!' 
          : 'Assessment completed successfully!'
      );
    } catch (error) {
      logger.error('Error completing assessment:', error);
      
      // Check if it's a token validation error from backend
      if (error.response?.data?.detail?.includes('Token validation failed')) {
        toast.error(
          t('messages.token_distribution_error', 'Error: La distribución de tokens debe sumar exactamente 100')
        );
      } else {
        toast.error(
          t('messages.error_completing', 'Error al completar evaluación')
        );
        // Fallback to regular update
        updateProgress('results', finalProgress);
      }
    }
  };

  const prevSection = () => {
    const currentIndex = sections.indexOf(currentSection);
    if (currentIndex > 0) {
      const newSection = sections[currentIndex - 1];
      setCurrentSection(newSection);
      updateProgress(newSection, ((currentIndex) / sections.length) * 100);
    }
  };

  const canProceed = () => {
    switch (currentSection) {
      case 'method_selection':
        // Must have selected a method
        return methodSelected;
      case 'offers': {
        // For pairwise, the component handles its own flow
        if (assessmentMethod === 'pairwise') return true;
        if (calculateTotalTokens(ofertas) !== 100) return false;
        // Check minimum 1 token per capital
        const allCapitalKeys = [
          'capital_fisico', 'capital_humano', 'capital_manufacturado',
          'capital_financiero', 'capital_natural', 'capital_cultural',
          'capital_social', 'capital_conocimiento', 'capital_psicologico',
          'capital_espiritual'
        ];
        return allCapitalKeys.every(key => ofertas[key] >= 1);
      }
      case 'needs': {
        // For pairwise, the component handles its own flow
        if (assessmentMethod === 'pairwise') return true;
        if (calculateTotalTokens(requerimientos) !== 100) return false;
        // Check minimum 1 token per capital
        const allCapitalKeys = [
          'capital_fisico', 'capital_humano', 'capital_manufacturado',
          'capital_financiero', 'capital_natural', 'capital_cultural',
          'capital_social', 'capital_conocimiento', 'capital_psicologico',
          'capital_espiritual'
        ];
        return allCapitalKeys.every(key => requerimientos[key] >= 1);
      }
      case 'aspirational':
        // Aspirational questionnaire can be skipped or completed
        return true;
      default:
        return true;
    }
  };

  const renderTokenDistribution = (distribution, setter, title, isOferta = true) => {
    const totalTokens = calculateTotalTokens(distribution);
    const remainingTokens = 100 - totalTokens;
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
          
          {/* Token Status */}
          <div className="flex justify-center space-x-4 mb-4">
            <Badge variant={totalTokens === 100 ? "success" : totalTokens > 100 ? "destructive" : "warning"}>
              {t('messages.total_tokens', 'Total')}: {totalTokens}/100
            </Badge>
            <Badge variant={remainingTokens === 0 ? "success" : remainingTokens < 0 ? "destructive" : "secondary"}>
              {t('messages.tokens_remaining', 'Restantes')}: {remainingTokens}
            </Badge>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center space-x-2 mb-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => resetTokens(isOferta)}
              data-testid={`reset-tokens-${isOferta ? 'offers' : 'needs'}`}
            >
              {t('reset_tokens', 'Reiniciar')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => distributeEvenly(isOferta)}
              data-testid={`distribute-evenly-${isOferta ? 'offers' : 'needs'}`}
            >
              {t('distribute_evenly', 'Distribuir Equitativamente')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => adjustToTotal(isOferta)}
              data-testid={`adjust-total-${isOferta ? 'offers' : 'needs'}`}
            >
              {t('adjust_to_100', 'Ajustar a 100')}
            </Button>
          </div>
          
          {/* Validation Messages */}
          {totalTokens !== 100 && (
            <div className={`mb-4 p-3 rounded-lg ${
              totalTokens > 100 
                ? 'bg-red-50 border border-red-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <p className={`text-sm ${totalTokens > 100 ? 'text-red-700' : 'text-amber-700'}`}>
                {totalTokens > 100 
                  ? `${t('messages.too_many_tokens', 'Has asignado')} ${totalTokens - 100} ${t('messages.tokens_over', 'tokens de más')}`
                  : `${t('messages.assign_all_tokens', 'Te faltan')} ${100 - totalTokens} ${t('messages.tokens_left', 'tokens por asignar')}`
                }
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {t('token_help', 'Usa los botones de arriba para ajustar automáticamente o modifica los valores manualmente')}
              </p>
            </div>
          )}
        </div>

        <div className="grid gap-6">
          {metaCapitals.map((capital, index) => {
            // Map capital names to backend keys regardless of language
            const capitalKeyMap = {
              'Capital Físico': 'capital_fisico',
              'Physical Capital': 'capital_fisico',
              'Capital Humano': 'capital_humano', 
              'Human Capital': 'capital_humano',
              'Herramientas': 'capital_manufacturado',
              'Tools': 'capital_manufacturado',
              'Manufactured Capital': 'capital_manufacturado',
              'Capital Financiero': 'capital_financiero',
              'Financial Capital': 'capital_financiero',
              'Capital Natural': 'capital_natural',
              'Natural Capital': 'capital_natural',
              'Capital Cultural': 'capital_cultural',
              'Cultural Capital': 'capital_cultural',
              'Capital Social': 'capital_social',
              'Social Capital': 'capital_social',
              'Capital Conocimiento': 'capital_conocimiento',
              'Knowledge Capital': 'capital_conocimiento',
              'Capital Psicológico': 'capital_psicologico',
              'Psychological Capital': 'capital_psicologico',
              'Propósito': 'capital_espiritual',
              'Purpose': 'capital_espiritual'
            };
            const capitalKey = capitalKeyMap[capital.name] || `capital_${capital.name.toLowerCase().replace('capital ', '').replace(' ', '_')}`;
            const capitalValue = distribution[capitalKey] || 0;
            const maxValue = Math.min(50, remainingTokens + capitalValue);
            
            return (
              <div key={index} className="bg-white p-4 rounded-lg border hover:shadow-md transition-shadow">
                <div className="flex items-center mb-3">
                  <span className="text-2xl mr-3">{capital.icon}</span>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{capital.name}</h4>
                    <p className="text-sm text-gray-600">{capital.definition}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={capitalValue}
                        onChange={(e) => handleTokenChange(capitalKey, parseInt(e.target.value) || 0, isOferta)}
                        className="w-16 px-2 py-1 border rounded text-center text-sm"
                        data-testid={`input-${capitalKey}`}
                      />
                      <span className="text-sm text-gray-500">tokens</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">{capital.quick_prompt}</p>
                  <div className="flex items-center space-x-3">
                    <Slider
                      value={[capitalValue]}
                      onValueChange={(value) => handleTokenChange(capitalKey, value[0], isOferta)}
                      max={maxValue}
                      min={0}
                      step={1}
                      className="flex-1"
                      data-testid={`slider-${capitalKey}`}
                    />
                    <span className="text-sm text-gray-500 min-w-[60px]">
                      0 - {maxValue}
                    </span>
                  </div>
                  
                  {/* Quick action buttons for this capital */}
                  <div className="flex space-x-1 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTokenChange(capitalKey, 0, isOferta)}
                      className="h-6 px-2 text-xs"
                    >
                      0
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTokenChange(capitalKey, Math.min(5, maxValue), isOferta)}
                      className="h-6 px-2 text-xs"
                    >
                      5
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTokenChange(capitalKey, Math.min(10, maxValue), isOferta)}
                      className="h-6 px-2 text-xs"
                    >
                      10
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTokenChange(capitalKey, Math.min(15, maxValue), isOferta)}
                      className="h-6 px-2 text-xs"
                    >
                      15
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTokenChange(capitalKey, Math.min(25, maxValue), isOferta)}
                      className="h-6 px-2 text-xs"
                    >
                      25
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (currentSection) {
      case 'personal_data':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl mb-4">👋</div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('sections.personal_data', 'Datos Personales')}
            </h2>
            <div className="max-w-md mx-auto space-y-4">
              <p className="text-gray-600">
                {t('personal_data_intro', 'Bienvenido a tu evaluación NotHR. Vamos a comenzar con algunos datos básicos.')}
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900">{t('your_profile', 'Tu Perfil')}</h3>
                <p className="text-blue-700">{user?.nombre}</p>
                <p className="text-blue-700">{user?.email}</p>
                {user?.empresa_nombre && (
                  <p className="text-blue-700">{user.empresa_nombre}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'introduction':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl mb-4">🎯</div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('sections.introduction', 'Introducción')}
            </h2>
            <div className="max-w-2xl mx-auto space-y-4 text-left">
              <p className="text-gray-600">
                {t('assessment_ui.intro_text_1', 'Esta evaluación mide tu perfil de Dimensiones - 10 tipos diferentes de recursos y capacidades que puedes aportar y que necesitas en tu trabajo ideal.')}
              </p>
              <p className="text-gray-600">
                {t('intro_text_2', 'Tendrás 100 tokens para distribuir en cada sección, representando la importancia relativa de cada capital para ti.')}
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-medium text-green-900">{t('assessment_info', 'Información de la Evaluación')}</h3>
                <p className="text-green-700">
                  {t('type', 'Tipo')}: {assessment?.tipo?.replace('_', ' ')?.toUpperCase()}
                </p>
                <p className="text-green-700">
                  {t('language', 'Idioma')}: {assessment?.idioma?.toUpperCase()}
                </p>
                <p className="text-green-700">
                  {t('estimated_time', 'Tiempo estimado')}: {assessment?.tipo?.includes('complete') ? '45-50 min' : '15-20 min'}
                </p>
              </div>
            </div>
          </div>
        );

      case 'method_selection':
        return (
          <div className="space-y-6">
            <AssessmentMethodSelector 
              onSelectMethod={(method) => {
                setAssessmentMethod(method);
                setMethodSelected(true);
              }}
              defaultMethod={assessmentMethod}
            />
            {methodSelected && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-4">
                  {language === 'es' 
                    ? `Método seleccionado: ${assessmentMethod === 'tokens' ? 'Distribución de Puntos' : 'Comparación por Pares'}`
                    : `Selected method: ${assessmentMethod === 'tokens' ? 'Point Distribution' : 'Pairwise Comparison'}`
                  }
                </p>
              </div>
            )}
          </div>
        );

      case 'offers':
        // If pairwise method selected, show pairwise assessment
        if (assessmentMethod === 'pairwise') {
          return (
            <PairwiseAssessment 
              assessmentId={assessmentId}
              onComplete={() => {
                // Navigate to results after pairwise completion
                setCurrentSection('results');
                completeAssessment();
              }}
            />
          );
        }
        // Default token distribution
        return (
          <AssessmentTokenUI
            values={ofertas}
            onValuesChange={setOfertas}
            isOferta={true}
            onComplete={nextSection}
            assessmentType={assessment?.tipo || ''}
            context="offers"
          />
        );

      case 'needs':
        return (
          <AssessmentTokenUI
            values={requerimientos}
            onValuesChange={setRequerimientos}
            isOferta={false}
            onComplete={nextSection}
            assessmentType={assessment?.tipo || ''}
            context="needs"
          />
        );

      case 'aspirational':
        return (
          <div className="space-y-6">
            {aspirationalCompleted ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎯</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'es' 
                    ? '¡Cuestionario de aspiraciones completado!' 
                    : 'Aspirations questionnaire completed!'}
                </h2>
                <p className="text-gray-600">
                  {language === 'es'
                    ? 'Tu perfil objetivo ha sido calculado. Continúa para validar tu evaluación.'
                    : 'Your objective profile has been calculated. Continue to validate your assessment.'}
                </p>
              </div>
            ) : (
              <EmployeeAspirationalQuestionnaire
                userId={user?.id}
                cicloId={assessment?.ciclo_id}
                showTransition={true}
                onComplete={(result) => {
                  setAspirationalCompleted(true);
                  toast.success(
                    language === 'es'
                      ? '¡Perfil de aspiraciones guardado!'
                      : 'Aspirations profile saved!'
                  );
                }}
              />
            )}
          </div>
        );

      case 'validation':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-gray-900">
                {t('sections.validation', 'Validación')}
              </h2>
              <p className="text-gray-600 mt-2">
                {t('validation_intro', 'Revisa tu distribución de tokens antes de finalizar')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Ofertas Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.offers', 'Lo que puedes aportar')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metaCapitals.map((capital, index) => {
                      // Map capital names to backend keys regardless of language
            const capitalKeyMap = {
              'Capital Físico': 'capital_fisico',
              'Physical Capital': 'capital_fisico',
              'Capital Humano': 'capital_humano', 
              'Human Capital': 'capital_humano',
              'Herramientas': 'capital_manufacturado',
              'Tools': 'capital_manufacturado',
              'Manufactured Capital': 'capital_manufacturado',
              'Capital Financiero': 'capital_financiero',
              'Financial Capital': 'capital_financiero',
              'Capital Natural': 'capital_natural',
              'Natural Capital': 'capital_natural',
              'Capital Cultural': 'capital_cultural',
              'Cultural Capital': 'capital_cultural',
              'Capital Social': 'capital_social',
              'Social Capital': 'capital_social',
              'Capital Conocimiento': 'capital_conocimiento',
              'Knowledge Capital': 'capital_conocimiento',
              'Capital Psicológico': 'capital_psicologico',
              'Psychological Capital': 'capital_psicologico',
              'Propósito': 'capital_espiritual',
              'Purpose': 'capital_espiritual'
            };
            const capitalKey = capitalKeyMap[capital.name] || `capital_${capital.name.toLowerCase().replace('capital ', '').replace(' ', '_')}`;
                      const value = ofertas[capitalKey] || 0;
                      if (value === 0) return null;
                      
                      return (
                        <div key={index} className="flex justify-between items-center">
                          <span className="flex items-center">
                            <span className="mr-2">{capital.icon}</span>
                            {capital.name}
                          </span>
                          <Badge>{value} tokens</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Requerimientos Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('sections.needs', 'Lo que necesitas')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metaCapitals.map((capital, index) => {
                      // Map capital names to backend keys regardless of language
            const capitalKeyMap = {
              'Capital Físico': 'capital_fisico',
              'Physical Capital': 'capital_fisico',
              'Capital Humano': 'capital_humano', 
              'Human Capital': 'capital_humano',
              'Herramientas': 'capital_manufacturado',
              'Tools': 'capital_manufacturado',
              'Manufactured Capital': 'capital_manufacturado',
              'Capital Financiero': 'capital_financiero',
              'Financial Capital': 'capital_financiero',
              'Capital Natural': 'capital_natural',
              'Natural Capital': 'capital_natural',
              'Capital Cultural': 'capital_cultural',
              'Cultural Capital': 'capital_cultural',
              'Capital Social': 'capital_social',
              'Social Capital': 'capital_social',
              'Capital Conocimiento': 'capital_conocimiento',
              'Knowledge Capital': 'capital_conocimiento',
              'Capital Psicológico': 'capital_psicologico',
              'Psychological Capital': 'capital_psicologico',
              'Propósito': 'capital_espiritual',
              'Purpose': 'capital_espiritual'
            };
            const capitalKey = capitalKeyMap[capital.name] || `capital_${capital.name.toLowerCase().replace('capital ', '').replace(' ', '_')}`;
                      const value = requerimientos[capitalKey] || 0;
                      if (value === 0) return null;
                      
                      return (
                        <div key={index} className="flex justify-between items-center">
                          <span className="flex items-center">
                            <span className="mr-2">{capital.icon}</span>
                            {capital.name}
                          </span>
                          <Badge>{value} tokens</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('sections.results', 'Resultados')}
            </h2>
            <p className="text-gray-600">
              {t('assessment_completed', '¡Evaluación completada exitosamente!')}
            </p>
            <div className="bg-green-50 p-6 rounded-lg max-w-md mx-auto">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h3 className="font-medium text-green-900 mb-2">
                {t('assessment_complete', 'Evaluación Completa')}
              </h3>
              <p className="text-green-700">
                {t('results_ready', 'Tus resultados están listos para ser analizados')}
              </p>
            </div>
            <Button onClick={() => navigate('/dashboard')}>
              {t('back_to_dashboard', 'Volver al Dashboard')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Hidden during offers/needs/results sections (and pairwise mode) */}
      {!(currentSection === 'offers' || currentSection === 'needs' || currentSection === 'results' || (assessmentMethod === 'pairwise' && (currentSection === 'offers' || currentSection === 'needs'))) && (
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('back', 'Volver')}
              </Button>
              
              <div className="flex items-center space-x-4">
                {saving && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Save className="h-4 w-4 mr-1 animate-pulse" />
                    {t('messages.auto_saved', 'Guardando...')}
                  </div>
                )}
                
                {/* Language Selector */}
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {language === 'es' ? 'EN' : 'ES'}
                  </Button>
                </div>
                
                <div className="text-sm text-gray-600">
                  <Clock className="h-4 w-4 inline mr-1" />
                  {sections.indexOf(currentSection) + 1} / {sections.length}
                </div>
              </div>
            </div>
            
            <div className="pb-4">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-gray-600 mt-2 text-center">
                {Math.round(progress)}% {t('completed', 'completado')}
              </p>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      {(currentSection === 'offers' || currentSection === 'needs') ? (
        // Full-screen mode for new assessment UI
        <main style={{ height: 'calc(100vh - 140px)' }}>
          {renderSection()}
        </main>
      ) : (
        // Card mode for other sections
        <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8">
              {renderSection()}
            </CardContent>
          </Card>

          {/* Navigation - Hidden in results section */}
          {currentSection !== 'results' && (
            <div className="flex justify-between mt-8">
              <Button 
                variant="outline" 
                onClick={prevSection}
                disabled={sections.indexOf(currentSection) === 0}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('buttons.previous', 'Anterior')}
              </Button>
              
              <Button 
                onClick={nextSection}
                disabled={sections.indexOf(currentSection) === sections.length - 1 || !canProceed()}
                data-testid="next-section-button"
              >
                {t('buttons.next', 'Siguiente')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
          
          {!canProceed() && (
            <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-amber-600 mr-2" />
                <p className="text-amber-700">
                  {t('messages.validation_error', 'Por favor completa esta sección antes de continuar')}
                </p>
              </div>
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default AssessmentFlow;